import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { updateJobService } from "requesters/jobServiceRequester";
import {
  ACCOUNTS_MANAGER, OPERATION_MANAGER, OPERATIONS, SURVEYORS_MANAGER, ACCOUNTS, ADMIN,
  ADMIN_3D_MANAGER,
  ROSS_USER_EMAILS
} from "utils/constants";
import { apiAttr, roleCheck } from "utils/index";
import { getBaseUrl, getCurrentUser, getCurrentRole } from "../../../reducers";

const EditProjectModal = ({
  show, onHide, job, reloadData, jobService, limitedInfo
}) => {
  const [loading, setLoading] = useState(false)
  const baseUrl = useSelector(getBaseUrl)
  const currentRole = useSelector(getCurrentRole)
  const currentUser = useSelector(getCurrentUser)
  const { accountTypes } = useSelector(state => state.options);
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role
  const isAccount = roleCheck(systemRole, currentRole, ACCOUNTS)
  const isOperations = roleCheck(systemRole, currentTeamRole, OPERATION_MANAGER)
  const isOperationsRole = roleCheck(systemRole, currentTeamRole, OPERATIONS)
  const is3DAdminManager = roleCheck(systemRole, currentTeamRole, ADMIN_3D_MANAGER)
  const userEmail = (currentUser.attributes.email || "").toLowerCase()
  const normalizedSystemRole = (systemRole || "").toLowerCase()
  const canEditQuoteMarkFields = ["admin", "super_admin"].includes(normalizedSystemRole)
    || currentRole?.includes(ADMIN)
    || ROSS_USER_EMAILS.some(e => (e || "").toLowerCase() === userEmail)

  const accountTypeOptions = accountTypes.map(acc => ({ value: acc[0], label: acc[1] }));
  const {
    attributes: {
      jobNo,
      jobAddress,
      clientType,
      jobTitle,
      leadType,
    }
  } = job

  const {
    id, attributes: {
      clientDueDateCount,
      estimatedHours,
      jobServiceLabel,
      jobNotes,
      forProjectX: jobForProjectX,
      canChangeProjectXFlag,
      cost,
      operationValue,
      operationsNotes,
      clientNotes,
      label,
      accountType,
      accountTypeId,
      unmatchedInvoiceFlagged,
      isUnmatchedInvoice,
      disabledNotMatchAmount,
      canBeMovedForward,
    }
  } = jobService
  const jsAttrs = jobService.attributes
  const [currentNotes, setCurrentNotes] = useState(jobNotes)
  const [currentLabel, setCurrentLabel] = useState(jobServiceLabel)
  const [newEstimatedHours, setNewEstimatedHours] = useState(estimatedHours)
  const [newClientDueDateCount, setNewClientDueDateCount] = useState(clientDueDateCount)
  const [newJobTitle, setNewJobTitle] = useState(jobTitle)
  const [currentOperationsNotes, setCurrentOperationsNotes] = useState(operationsNotes)
  const [currentClientNotes, setCurrentClientNotes] = useState(clientNotes)
  const [unMatchedInvoice, setUnMatchedInvoice] = useState(unmatchedInvoiceFlagged)
  const [canBeMovedForwardValue, setCanBeMovedForwardValue] = useState(canBeMovedForward)
  const [forProjectX, setForProjectX] = useState(jobForProjectX)
  const [newOperationValue, setNewOperationValue] = useState(operationValue)

  const [currentAccountType, setCurrentAccountType] = useState(accountTypeId);

  const initialQuotedMarksFromJobService = () => (
    apiAttr(jsAttrs, "quotedMarks") ?? ""
  )
  const initialCostPerMarkFromJobService = () => (
    apiAttr(jsAttrs, "costPerMark") ?? ""
  )
  const [quotedMarksDraft, setQuotedMarksDraft] = useState(() => initialQuotedMarksFromJobService())
  const [costPerMarkDraft, setCostPerMarkDraft] = useState(() => initialCostPerMarkFromJobService())

  useEffect(() => {
    if (!show) return
    setCurrentNotes(jobNotes)
    setCurrentOperationsNotes(operationsNotes)
    setCurrentClientNotes(clientNotes)
    setQuotedMarksDraft(initialQuotedMarksFromJobService())
    setCostPerMarkDraft(initialCostPerMarkFromJobService())
  }, [show, jobService, jobNotes, operationsNotes, clientNotes])

  const normalizeQuoteFieldStr = val => (
    val === null || val === undefined ? "" : String(val).trim()
  )

  const buildJobServiceQuoteMetricsPayload = () => {
    if (!canEditQuoteMarkFields) return {}

    const patch = {}
    const qmRaw = normalizeQuoteFieldStr(quotedMarksDraft)
    if (qmRaw !== "") {
      const qm = parseInt(qmRaw, 10)
      if (!Number.isNaN(qm)) patch.quoted_marks = qm
    }
    const cpmRaw = normalizeQuoteFieldStr(costPerMarkDraft)
    if (cpmRaw !== "") {
      const cpm = parseFloat(cpmRaw)
      if (!Number.isNaN(cpm)) patch.cost_per_mark = cpm
    }
    return patch
  }

  const buildChangedNoteFieldsPayload = () => {
    const noteFields = {}
    if (currentNotes !== jobNotes) noteFields.notes = currentNotes
    if (currentOperationsNotes !== operationsNotes) noteFields.operations_notes = currentOperationsNotes
    if (currentClientNotes !== clientNotes) noteFields.client_notes = currentClientNotes
    return noteFields
  }

  const validateForm = () => {
    setLoading(true)
    const payload = {
      ...buildChangedNoteFieldsPayload(),
      client_due_date_count: newClientDueDateCount,
      estimated_hours: newEstimatedHours,
      job_title: newJobTitle,
      job_service_label: currentLabel,
      account_type: currentAccountType,
      disabled_not_match_amount: isUnmatchedInvoice ? !unMatchedInvoice : disabledNotMatchAmount,
      can_be_moved_forward: canBeMovedForwardValue,
      for_project_x: forProjectX,
      operation_value: newOperationValue,
      ...buildJobServiceQuoteMetricsPayload(),
    }

    updateJobService(baseUrl, id, payload)
      .then(result => {
        const ok = result.status === 200 && !result.error
        if (ok) {
          reloadData()
          onHide()
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title><h2 className="modal-title">EDIT PROJECT</h2></Modal.Title>
      </Modal.Header>
      <div className="modal-body px-md-5">
        <form>
          { isOperations && (
            <>
              <div className="row mb-2">
                <label className="col-4">Job Title</label>
                <div className="col-6">
                  <input className="form-control" value={newJobTitle} onChange={e => setNewJobTitle(e.target.value)} />
                </div>
              </div>
              <div className="row">
                <label className="col-4">Job Address</label>
                <div className="col-8">
                  <p className="mb-0">{jobAddress}</p>
                </div>
              </div>
              <div className="row">
                <label className="col-4">Job No</label>
                <div className="col-6">
                  <p className="mb-0">{jobNo}</p>
                </div>
              </div>

              <div className="row mb-2">
                <label className="col-4">Operation Value</label>
                <div className="col-6">
                  <input type="number" inputMode="decimal" min={0} className="form-control" value={newOperationValue} onChange={e => setNewOperationValue(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {!limitedInfo && (
            <>
              <div className="row">
                <label className="col-4">Value</label>
                <div className="col-6">
                  <p className="mb-0">{`$${cost}`}</p>
                </div>
              </div>
              <div className="row">
                <label className="col-4">Client Type</label>
                <div className="col-6">
                  <p className="mb-0">{clientType}</p>
                </div>
              </div>
              <div className="row">
                <label className="col-4">Lead Type</label>
                <div className="col-6">
                  <p className="mb-0">{leadType}</p>
                </div>
              </div>
              <div className="row mb-2">
                <label className="col-4">Account Type</label>
                <div className="col-6">
                  { isAccount ? (
                    <Select
                      name="account_type"
                      placeholder="Select"
                      isDisabled={!isAccount}
                      defaultValue={accountTypeOptions.find(option => option.value === accountTypeId)}
                      value={accountTypeOptions.find(option => option.value === currentAccountType)}
                      options={accountTypeOptions}
                      onChange={e => setCurrentAccountType(e ? e.value : "")} />
                  ) : (
                    <p className="mb-0">{accountType}</p>
                  )}
                </div>
              </div>
            </>
          )}

          { isOperations && (
            <div className="row">
              <label className="col-4">Job Service</label>
              <div className="col-8">
                <p className="mb-0">{label}</p>
              </div>
            </div>
          )}

          {(!limitedInfo || is3DAdminManager) && (
            <>
              <div className="row mb-2">
                <label className="col-4">Estimated Hours</label>
                <div className="col-6">
                  <input className="form-control" value={newEstimatedHours} onChange={e => setNewEstimatedHours(e.target.value)} />
                </div>
              </div>

              <div className="row mb-2">
                <label className="col-4">Client Due Date Count</label>
                <div className="col-6">
                  <input className="form-control" value={newClientDueDateCount} onChange={e => setNewClientDueDateCount(e.target.value)} />
                </div>
              </div>

            </>
          )}

          {canEditQuoteMarkFields && (
            <>
              <div className="row mb-2">
                <label className="col-4">Quoted Mark</label>
                <div className="col-6">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="form-control"
                    value={quotedMarksDraft}
                    placeholder="Quoted mark count"
                    onChange={e => setQuotedMarksDraft(e.target.value)} />
                </div>
              </div>
              <div className="row mb-2">
                <label className="col-4">Dollar Value</label>
                <div className="col-6">
                  <input
                    type="number"
                    min={0}
                    inputMode="decimal"
                    step="0.01"
                    className="form-control"
                    value={costPerMarkDraft}
                    placeholder="Per mark ($)"
                    onChange={e => setCostPerMarkDraft(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {(isOperations || roleCheck(systemRole, currentTeamRole, SURVEYORS_MANAGER))
          ? (
            <div className="row">
              <label className="col-4">Job Label</label>
              <div className="col-6">
                <input
                  className="form-control mr-5"
                  placeholder="Enter Job Label..."
                  value={currentLabel || ""}
                  maxLength={60}
                  onChange={e => setCurrentLabel(e.target.value)} />
              </div>
            </div>
          ) : null}


          { (isOperations || isOperationsRole || currentRole?.includes(ADMIN)) && (
            <div className="row mt-2">
              <label className="col-4">Project X</label>
              <div className="col-6">
                <input
                  disabled={!canChangeProjectXFlag}
                  checked={forProjectX}
                  className="w-20px h-20px"
                  type="checkbox"
                  onChange={e => setForProjectX(e.target.checked)} />
              </div>
            </div>
          )}

          {(isAccount || currentRole?.includes(ADMIN) || isOperations) && isUnmatchedInvoice && (
            <div className="row mt-2">
              <label className="col-4">Unmatched Invoice</label>
              <div className="col-6">
                <input
                  checked={unMatchedInvoice}
                  className="w-20px h-20px"
                  type="checkbox"
                  onChange={e => setUnMatchedInvoice(e.target.checked)} />
              </div>
            </div>
          )}

          {(isOperations || currentRole?.includes(ADMIN)) && (
            <div className="row mt-2">
              <label className="col-4">Booking can be moved forward</label>
              <div className="col-6">
                <input
                  checked={canBeMovedForwardValue}
                  className="w-20px h-20px"
                  type="checkbox"
                  onChange={e => setCanBeMovedForwardValue(e.target.checked)} />
              </div>
            </div>
          )}

          { isOperations && (
            <div className="form-group">
              <label>Job Comments</label>
              <ReactQuill
                placeholder="e.g. existing dwelling, any substantial trees"
                value={currentNotes || ""}
                onChange={value => setCurrentNotes(value)} />
            </div>
          )}

          {(isOperations || roleCheck(systemRole, currentTeamRole, ACCOUNTS_MANAGER)) && (
            <>
              <div className="form-group">
                <label>Operations Notes</label>
                <ReactQuill
                  placeholder="Enter Operations Notes..."
                  value={currentOperationsNotes || ""}
                  onChange={value => setCurrentOperationsNotes(value)} />
              </div>
              <div className="form-group">
                <label>Client Notes</label>
                <ReactQuill
                  placeholder="Enter Client Notes..."
                  value={currentClientNotes || ""}
                  onChange={value => setCurrentClientNotes(value)} />
              </div>
            </>
          )}
        </form>
        <div className="modal-footer d-flex justify-content-between p-0 mb-4 mt-5">
          <button className="btn btn-light btn-lg" data-dismiss="modal" type="button" onClick={onHide}> Cancel</button>
          <button
            className="btn btn-red btn-lg"
            onClick={validateForm}
            disabled={loading || (
              unMatchedInvoice === unmatchedInvoiceFlagged
              && currentOperationsNotes === operationsNotes && currentClientNotes === clientNotes && currentNotes === jobNotes
              && newEstimatedHours === estimatedHours && newClientDueDateCount === clientDueDateCount && newJobTitle === jobTitle
              && currentLabel === jobServiceLabel && currentAccountType === accountTypeId && canBeMovedForward === canBeMovedForwardValue
              && jobForProjectX === forProjectX && newOperationValue === operationValue
              && normalizeQuoteFieldStr(quotedMarksDraft) === normalizeQuoteFieldStr(initialQuotedMarksFromJobService())
              && normalizeQuoteFieldStr(costPerMarkDraft) === normalizeQuoteFieldStr(initialCostPerMarkFromJobService())
            )}
            type="submit">
            Save Changes
            {loading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
          </button>
        </div>
      </div>
    </Modal>
  )
}
export default EditProjectModal
